import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Truck, Award, ShieldCheck, Headphones } from "lucide-react";
import { Button } from "@/components/ui/button";
import ProductCard from "@/components/ProductCard";
import api from "@/lib/api";

const HeroSection = () => (
  <section className="relative overflow-hidden bg-gradient-to-br from-[#0b3d75] via-[#0e4a8c] to-[#1266b8] text-white">
    <div
      className="absolute inset-0 opacity-20"
      style={{
        backgroundImage: "url('https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?w=1600')",
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    />
    <div className="absolute inset-0 bg-gradient-to-r from-[#0b3d75] via-[#0b3d75]/70 to-transparent" />
    <div className="relative max-w-7xl mx-auto px-4 py-16 md:py-24 grid md:grid-cols-2 gap-10 items-center">
      <div>
        <span className="inline-block px-3 py-1 rounded-full bg-amber-400/20 text-amber-200 border border-amber-300/30 text-xs font-semibold uppercase tracking-widest">
          Family-Owned • Trusted Since 2010
        </span>
        <h1 className="mt-4 text-4xl md:text-6xl font-black leading-tight tracking-tight">
          Medical Supplies,<br />
          <span className="text-amber-300">Delivered With Care.</span>
        </h1>
        <p className="mt-5 text-lg text-slate-100/90 max-w-lg">
          Shop 10,000+ trusted brands of home medical equipment, wound care,
          incontinence, mobility aids, and daily essentials — all backed by our
          expert team.
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <Button asChild size="lg" className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold text-base h-12 px-6">
            <Link to="/shop">
              Shop All Products <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
          </Button>
          <Button asChild variant="outline" size="lg" className="h-12 px-6 bg-white/10 text-white border-white/30 hover:bg-white/20 hover:text-white">
            <Link to="/shop/incontinence">Browse Incontinence</Link>
          </Button>
        </div>

        <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { icon: Truck, t: "Free Shipping", s: "On $75+" },
            { icon: Award, t: "Trusted Brands", s: "2,500+" },
            { icon: ShieldCheck, t: "Secure", s: "Square Pay" },
            { icon: Headphones, t: "Support", s: "Mon–Fri" },
          ].map(({ icon: Icon, t, s }) => (
            <div key={t} className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center shrink-0">
                <Icon className="w-4 h-4 text-amber-300" />
              </div>
              <div className="text-sm">
                <div className="font-bold">{t}</div>
                <div className="text-slate-200 text-xs">{s}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="hidden md:block relative">
        <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-white/20">
          <img
            src="https://images.pexels.com/photos/33055509/pexels-photo-33055509.jpeg?w=800"
            alt="Medical professionals"
            className="w-full h-[420px] object-cover"
          />
        </div>
        <div className="absolute -bottom-6 -left-6 bg-white text-slate-900 rounded-xl shadow-xl p-4 border border-slate-100 max-w-xs">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-full bg-amber-400 flex items-center justify-center">
              <Award className="w-5 h-5 text-slate-900" />
            </div>
            <div>
              <div className="font-black text-sm">Authorized Distributor</div>
              <div className="text-xs text-slate-500">Medline • 3M • Convatec • Hollister</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const FeatureStrip = () => (
  <section className="bg-white border-b border-slate-200">
    <div className="max-w-7xl mx-auto px-4 py-6 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
      {[
        { title: "Over 100,000 Products", sub: "Largest medical supply catalog online" },
        { title: "Guaranteed Low Prices", sub: "Backed by our price-match promise" },
        { title: "Fast, Reliable Shipping", sub: "From nationwide warehouses" },
        { title: "Trusted by 2.5M+ Customers", sub: "Top-rated by BBB & Google" },
      ].map((f) => (
        <div key={f.title}>
          <div className="font-bold text-[#0b3d75] text-sm md:text-base">{f.title}</div>
          <div className="text-xs text-slate-500 mt-0.5">{f.sub}</div>
        </div>
      ))}
    </div>
  </section>
);

const CategoryGrid = ({ categories }) => (
  <section className="max-w-7xl mx-auto px-4 py-14">
    <div className="flex items-end justify-between mb-8">
      <div>
        <h2 className="text-3xl md:text-4xl font-black text-slate-900 tracking-tight">Browse by Category</h2>
        <p className="text-slate-600 mt-2">Low pricing, fast delivery, and expert-backed selection.</p>
      </div>
      <Link to="/shop" className="text-[#0b3d75] font-semibold hover:text-amber-600 hidden md:inline-flex items-center">
        View all <ArrowRight className="w-4 h-4 ml-1" />
      </Link>
    </div>
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
      {categories.map((c) => (
        <Link
          key={c.slug}
          to={`/shop/${c.slug}`}
          className="group relative aspect-[4/3] rounded-xl overflow-hidden border border-slate-200 bg-white hover:shadow-xl hover:border-[#0b3d75]/40 transition-all duration-200"
        >
          <img
            src={c.image}
            alt={c.name}
            className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/85 via-slate-900/30 to-transparent" />
          <div className="absolute inset-x-0 bottom-0 p-4">
            <div className="text-white font-bold text-lg">{c.name}</div>
            <div className="text-slate-300 text-xs mt-1">
              {c.subcategories?.length || 0} subcategories →
            </div>
          </div>
        </Link>
      ))}
    </div>
  </section>
);

const FeaturedProducts = ({ products, title, cta }) => (
  <section className="bg-slate-50 py-14">
    <div className="max-w-7xl mx-auto px-4">
      <div className="flex items-end justify-between mb-8">
        <div>
          <h2 className="text-3xl md:text-4xl font-black text-slate-900 tracking-tight">{title}</h2>
          <p className="text-slate-600 mt-2">Trending medical supplies our customers love.</p>
        </div>
        {cta && (
          <Link to={cta.href} className="text-[#0b3d75] font-semibold hover:text-amber-600 hidden md:inline-flex items-center">
            {cta.label} <ArrowRight className="w-4 h-4 ml-1" />
          </Link>
        )}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {products.map((p) => (
          <ProductCard key={p.id} product={p} />
        ))}
      </div>
    </div>
  </section>
);

const BrandStrip = ({ brands }) => (
  <section className="max-w-7xl mx-auto px-4 py-14">
    <h2 className="text-2xl md:text-3xl font-black text-slate-900 tracking-tight text-center">
      Authorized Distributor of Top Medical Brands
    </h2>
    <p className="text-center text-slate-600 mt-2 mb-8 max-w-2xl mx-auto">
      We partner with manufacturers to bring you genuine products, every time.
    </p>
    <div className="grid grid-cols-3 md:grid-cols-7 gap-4">
      {brands.slice(0, 14).map((b) => (
        <div
          key={b}
          className="h-16 rounded-lg border border-slate-200 bg-white flex items-center justify-center text-slate-500 font-bold text-sm hover:border-[#0b3d75] hover:text-[#0b3d75] transition-colors"
        >
          {b}
        </div>
      ))}
    </div>
  </section>
);

const PromoBanner = () => (
  <section className="bg-gradient-to-r from-amber-400 to-amber-500 py-10">
    <div className="max-w-7xl mx-auto px-4 flex flex-col md:flex-row items-center justify-between gap-4">
      <div>
        <div className="text-xs font-bold uppercase tracking-widest text-amber-900">B2B Program</div>
        <div className="text-2xl md:text-3xl font-black text-slate-900 mt-1">
          Bulk Discounts for Businesses & Facilities
        </div>
        <div className="text-slate-800 mt-1">Save more when you buy in volume. Ask about our net-30 terms.</div>
      </div>
      <Button asChild className="bg-[#0b3d75] hover:bg-[#0a4e94] text-white h-12 px-6 font-bold">
        <Link to="/contact">Get a Quote <ArrowRight className="w-4 h-4 ml-2" /></Link>
      </Button>
    </div>
  </section>
);

const Home = () => {
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [popular, setPopular] = useState([]);

  useEffect(() => {
    api.get("/categories").then((res) => {
      setCategories(res.data.categories || []);
      setBrands(res.data.brands || []);
    });
    api.get("/products", { params: { featured: true, limit: 8 } }).then((res) => setFeatured(res.data.items || []));
    api.get("/products", { params: { limit: 8, sort: "price_asc" } }).then((res) => setPopular(res.data.items || []));
  }, []);

  return (
    <div>
      <HeroSection />
      <FeatureStrip />
      <CategoryGrid categories={categories} />
      {featured.length > 0 && (
        <FeaturedProducts
          products={featured}
          title="Featured Products"
          cta={{ label: "See all featured", href: "/shop?featured=true" }}
        />
      )}
      <PromoBanner />
      {popular.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 py-14">
          <div className="flex items-end justify-between mb-8">
            <div>
              <h2 className="text-3xl md:text-4xl font-black text-slate-900 tracking-tight">Everyday Essentials</h2>
              <p className="text-slate-600 mt-2">Affordable supplies you'll reach for again and again.</p>
            </div>
            <Link to="/shop" className="text-[#0b3d75] font-semibold hover:text-amber-600 hidden md:inline-flex items-center">
              Shop all <ArrowRight className="w-4 h-4 ml-1" />
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {popular.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        </section>
      )}
      <BrandStrip brands={brands} />
    </div>
  );
};

export default Home;
